import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Mail, RefreshCw } from 'lucide-react';

interface EmailLoginFormProps {
  onSuccess?: () => void;
  franchiseId?: string | null;
}

export function EmailLoginForm({ onSuccess, franchiseId }: EmailLoginFormProps) {
  const { t } = useTranslation();
  const { signInWithEmail, signUpWithEmail, resendVerificationEmail, refreshEmailVerification } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Guardar franchiseId en localStorage si existe
      if (franchiseId) {
        localStorage.setItem('pendingFranchiseId', franchiseId);
      }

      if (isSignUp) {
        // Sign up
        if (!formData.displayName.trim()) {
          toast.error(t('auth.enterName', 'Por favor ingresa tu nombre'));
          setLoading(false);
          return;
        }
        await signUpWithEmail(formData.email, formData.password, formData.displayName);
        toast.success(t('auth.accountCreated', 'Cuenta creada exitosamente'));
        // Mostrar pantalla de verificación
        setRegisteredEmail(formData.email);
        setShowVerificationScreen(true);
        return; // No llamar onSuccess aún
      } else {
        // Sign in
        await signInWithEmail(formData.email, formData.password);
        toast.success('Sesión iniciada correctamente');
      }
      onSuccess?.();
    } catch (error: any) {
      console.error('Error en autenticación con email:', error);

      // Firebase error messages
      let errorMessage = 'Error al iniciar sesión';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No existe una cuenta con este email';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Contraseña incorrecta';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email inválido';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Ya existe una cuenta con este email';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'La contraseña debe tener al menos 6 caracteres';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Credenciales inválidas. Verifica tu email y contraseña';
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerificationEmail();
      toast.success(t('auth.verificationEmailSent', 'Email de verificación enviado'));
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      if (error.code === 'auth/too-many-requests') {
        toast.error(t('auth.tooManyRequests', 'Demasiados intentos. Espera unos minutos.'));
      } else {
        toast.error(t('auth.errorSendingEmail', 'Error al enviar el email'));
      }
    } finally {
      setResending(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const verified = await refreshEmailVerification();
      if (verified) {
        toast.success(t('auth.emailVerified', 'Email verificado correctamente'));
        onSuccess?.();
      } else {
        toast.info(t('auth.emailNotYetVerified', 'El email aún no ha sido verificado'));
      }
    } catch (error) {
      console.error('Error refreshing verification status:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Mostrar pantalla de verificación pendiente
  if (showVerificationScreen) {
    return (
      <div className="w-full max-w-sm text-center">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-info/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-info" />
          </div>
          <h2 className="text-xl font-semibold text-ink mb-2">
            {t('auth.verifyYourEmail', 'Verifica tu email')}
          </h2>
          <p className="text-sm text-ink-muted">
            {t('auth.verificationSentTo', 'Hemos enviado un enlace de verificación a:')}
          </p>
          <p className="text-sm font-medium text-ink mt-1">
            {registeredEmail}
          </p>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleRefresh}
            isLoading={refreshing}
            variant="primary"
            className="w-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('auth.alreadyVerified', 'Ya verifiqué mi email')}
          </Button>

          <Button
            onClick={handleResend}
            isLoading={resending}
            variant="outline"
            className="w-full"
          >
            {t('auth.resendVerificationEmail', 'Reenviar email de verificación')}
          </Button>

          <button
            type="button"
            onClick={() => {
              setShowVerificationScreen(false);
              onSuccess?.();
            }}
            className="text-sm text-ink-subtle hover:text-ink "
          >
            {t('auth.continueWithoutVerifying', 'Continuar sin verificar')}
          </button>
        </div>

        <p className="mt-6 text-xs text-ink-subtle">
          {t('auth.checkSpamFolder', 'Si no ves el email, revisa tu carpeta de spam')}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        {isSignUp && (
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-ink-muted mb-2">
              Nombre completo
            </label>
            <input
              id="displayName"
              type="text"
              required={isSignUp}
              value={formData.displayName}
              onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
              placeholder="Juan Pérez"
            />
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-ink-muted mb-2">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            placeholder="admin@example.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-ink-muted mb-2">
            Contraseña
          </label>
          <input
            id="password"
            type="password"
            required
            value={formData.password}
            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            placeholder="••••••••"
            minLength={6}
          />
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-ink-subtle">
              Mínimo 6 caracteres
            </p>
            {!isSignUp && (
              <Link
                to="/forgot-password"
                className="text-xs text-info hover:underline"
              >
                Olvidé mi contraseña
              </Link>
            )}
          </div>
        </div>

        <Button
          type="submit"
          isLoading={loading}
          variant="primary"
          className="w-full"
        >
          {isSignUp ? 'Crear cuenta' : 'Iniciar sesión'}
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-sm text-info hover:underline"
          >
            {isSignUp ? '¿Ya tienes cuenta? Inicia sesión' : '¿No tienes cuenta? Regístrate'}
          </button>
        </div>
      </form>

    </div>
  );
}
